import { Fragment, FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    ActionIcon,
    Avatar,
    Box,
    Divider,
    Group,
    Image,
    Menu,
    Navbar,
    ScrollArea,
    Text,
    Title,
    Tooltip,
    UnstyledButton,
    useMantineColorScheme,
    useMantineTheme,
} from "@mantine/core";
import { useOs } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import {
    IconChevronCompactRight,
    IconLock,
    IconLogout,
    IconMoonStars,
    IconSun,
    IconUserCircle,
} from "@tabler/icons-react";

import { useAuthContext, useLogout } from "../../hooks/auth";
import { ClientLocale } from "../../types";
import { resolveCountryCode } from "../../utils/helpers";
import { SelectClient } from "../SelectClient";

export interface NavigationItem {
    title: string;
    isDivider?: boolean;
    path?: string;
    activeRegex?: RegExp;
    icon?: FunctionComponent;
}

export interface LayoutNavbarProps {
    isNavbarOpened: boolean;
    isMobile: boolean;
    items: NavigationItem[];
    onIsNavbarOpenedChange(isNavbarOpened: boolean);
}

export const LayoutNavbar = ({ isNavbarOpened, items, onIsNavbarOpenedChange, isMobile }: LayoutNavbarProps) => {
    const { t, i18n } = useTranslation(["common"]);

    const location = useLocation();
    const navigate = useNavigate();

    const os = useOs();
    const theme = useMantineTheme();
    const { colorScheme, toggleColorScheme } = useMantineColorScheme();

    const { user, client, setClient, initResetPassword } = useAuthContext();
    const { logout } = useLogout();

    const [, setIsLangOpen] = useState(false);

    // Change-password for a logged-in user: send a real reset link to their own
    // email (reuses the working forgot-password flow). The reset-confirm page
    // (/password-reset) only works with the ?token= carried by that email link —
    // navigating there directly leaves email/code empty and surfaces a misleading
    // "password cannot be empty" error (TTV2-1504).
    const handleChangePassword = async () => {
        onIsNavbarOpenedChange(false);

        if (!user?.email) {
            showNotification({
                color: "red",
                title: t("changepassword_error"),
                message: t("signin_fail_description"),
            });
            return;
        }

        try {
            await initResetPassword(user.email);
            showNotification({
                color: "green",
                message: t("resetpassword_sent"),
            });
        } catch {
            // initResetPassword already reports the error to Sentry; just surface
            // a user-facing failure notification here.
            showNotification({
                color: "red",
                title: t("changepassword_error"),
                message: t("resetpassword_failed"),
            });
        }
    };

    return (
        <Navbar
            style={{
                transition: "transform 0.2s ease",
                transform: isMobile ? `translateX(${isNavbarOpened ? 0 : -100}%)` : undefined,
            }}
            withBorder={false}
            hiddenBreakpoint="sm"
            width={{ sm: 240, lg: 240 }}
            pr="sm"
        >
            <Image
                src="/images/logo_client_zone.svg"
                width="auto"
                height={45}
                mx={25}
                mt={40}
                sx={{
                    "media (maxWidth: 768px)": {
                        display: "none",
                    },
                }}
            />
            <Text
                size="xl"
                weight={700}
                ml="xl"
                mt="sm"
                color={theme.primaryColor}
                py={5}
                px={8}
                w="fit-content"
                style={{
                    backgroundColor: theme.colors.primary[2],
                    borderRadius: "8px",
                }}
                mb={15}
            ></Text>
            <Navbar.Section pl={25}>
                <SelectClient
                    label={t("organization")}
                    placeholder={t("organization")}
                    value={client?.id}
                    onChange={(clientId, client) => {
                        setClient(client);
                        localStorage.setItem("__clientautoselectprecedence_id", client?.id);
                    }}
                    autoselectPrecedence={(client) =>
                        localStorage.getItem("__clientautoselectprecedence_id") === client?.id
                    }
                    displayRole
                    autoselect
                />
            </Navbar.Section>
            <Navbar.Section component={ScrollArea} mt="lg" mx="-xs" px="xs" grow>
                {items.map((item, index) => {
                    if (item?.isDivider) {
                        return (
                            <Fragment key={`${index}${item?.path}`}>
                                {item?.title && (
                                    <Title
                                        order={5}
                                        ml="xs"
                                        mt="md"
                                        style={{
                                            color:
                                                theme.colorScheme === "dark"
                                                    ? theme.colors.gray[5]
                                                    : theme.colors.gray[7],
                                        }}
                                    >
                                        {item?.title}
                                    </Title>
                                )}
                                <Divider
                                    my="xs"
                                    style={{
                                        borderTopColor:
                                            theme.colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[2],
                                    }}
                                />
                            </Fragment>
                        );
                    }

                    const isActive = item.activeRegex.test(location.pathname);

                    return (
                        <Link key={`${index}${item?.path}`} to={item.path} style={{ textDecoration: "none" }}>
                            <UnstyledButton
                                style={{
                                    display: "block",
                                    width: "100%",
                                    marginTop: theme.spacing.sm,
                                    padding: theme.spacing.xs,
                                    paddingLeft: theme.spacing.xl,
                                    borderRadius: "0px 20px 20px 0px",
                                    transition: "background-color 0.2s ease",
                                    color: isActive
                                        ? theme.colors.primary[0]
                                        : theme.colorScheme === "dark"
                                          ? theme.colors.dark[0]
                                          : theme.black,
                                    backgroundColor: isActive
                                        ? theme.colorScheme === "dark"
                                            ? theme.colors.dark[5]
                                            : theme.colors.primary[2]
                                        : undefined,
                                }}
                                sx={(theme) => ({
                                    "&:hover": isActive
                                        ? {}
                                        : {
                                              backgroundColor:
                                                  theme.colorScheme === "dark"
                                                      ? theme.colors.dark[6]
                                                      : theme.colors.gray[1],
                                          },
                                })}
                                onClick={() => {
                                    navigate(item.path);
                                    onIsNavbarOpenedChange(false);
                                }}
                            >
                                <Group>
                                    {item?.icon && (
                                        <Box
                                            component={item.icon}
                                            style={{
                                                strokeWidth: isActive ? 2 : 1.5,
                                                stroke: isActive
                                                    ? theme.colors.primary[0]
                                                    : theme.colorScheme === "dark"
                                                      ? theme.colors.dark[3]
                                                      : theme.colors.gray[7],
                                            }}
                                        />
                                    )}
                                    <Text size="sm" weight={600} color={isActive ? theme.colors.primary[0] : undefined}>
                                        {item?.title}
                                    </Text>
                                </Group>
                            </UnstyledButton>
                        </Link>
                    );
                })}
            </Navbar.Section>
            <Navbar.Section>
                <Box style={{ padding: "16px" }}>
                    <Menu position="right-end" withArrow>
                        <Menu.Target>
                            <UnstyledButton
                                sx={{
                                    display: "block",
                                    width: "100%",
                                    padding: theme.spacing.xs,
                                    borderRadius: theme.radius.sm,
                                    color: theme.colorScheme === "dark" ? theme.colors.dark[0] : theme.black,
                                    "&:hover": {
                                        backgroundColor:
                                            theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[1],
                                    },
                                }}
                            >
                                <Group>
                                    <Avatar size={40} radius="xl">
                                        {(user?.firstName || "").substring(0, 1).toUpperCase()}
                                        {(user?.lastName || "").substring(0, 1).toUpperCase()}
                                    </Avatar>
                                    <Box style={{ flex: 1 }}>
                                        <Text size={14} weight={500}>
                                            {user?.firstName} {user?.lastName}
                                        </Text>
                                        <Text
                                            onClick={(event) => {
                                                event?.stopPropagation?.();

                                                logout().then(() => {
                                                    onIsNavbarOpenedChange(false);
                                                    showNotification({
                                                        color: "green",
                                                        message: t("signout_success"),
                                                    });
                                                });
                                            }}
                                            style={{
                                                lineHeight: 1.2,
                                                transition: "color 0.2s ease",
                                            }}
                                            sx={{
                                                "&:hover": {
                                                    color: "orangered",
                                                },
                                            }}
                                            color="dimmed"
                                            size="xs"
                                        >
                                            {t("signout")}
                                        </Text>
                                    </Box>
                                    <IconChevronCompactRight size={16} color="#868e96" />
                                </Group>
                            </UnstyledButton>
                        </Menu.Target>
                        <Menu.Dropdown>
                            <Menu.Label>{t("useraccount")}</Menu.Label>
                            <Menu.Item
                                icon={<IconUserCircle size={16} />}
                                onClick={() => {
                                    navigate("/profile");
                                    onIsNavbarOpenedChange(false);
                                }}
                            >
                                {t("editprofile")}
                            </Menu.Item>
                            <Menu.Item icon={<IconLock size={16} />} onClick={handleChangePassword}>
                                {t("changepassword")}
                            </Menu.Item>
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <Menu
                                    radius="md"
                                    position="bottom-end"
                                    onOpen={() => setIsLangOpen(true)}
                                    onClose={() => setIsLangOpen(false)}
                                >
                                    <Menu.Target>
                                        <ActionIcon size={42} variant="subtle">
                                            <Image
                                                src={`/images/flags/${resolveCountryCode(i18n.language)}.svg`}
                                                width="auto"
                                                height={20}
                                                radius="sm"
                                            />
                                        </ActionIcon>
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                        {Object.values(ClientLocale).map((locale, index) => (
                                            <Menu.Item
                                                key={`${index}${locale}`}
                                                icon={
                                                    <Image
                                                        src={`/images/flags/${resolveCountryCode(locale)}.svg`}
                                                        width="auto"
                                                        height={18}
                                                        radius="sm"
                                                    />
                                                }
                                                onClick={() => i18n.changeLanguage(locale)}
                                            >
                                                {t(`lang_${locale}`)}
                                            </Menu.Item>
                                        ))}
                                    </Menu.Dropdown>
                                </Menu>
                                <Tooltip label={os === "macos" ? "⌘ + J" : "Ctrl + J"}>
                                    <ActionIcon
                                        size={42}
                                        ml="0.5rem"
                                        variant="subtle"
                                        onClick={() => toggleColorScheme()}
                                    >
                                        {colorScheme === "dark" ? <IconSun size={22} /> : <IconMoonStars size={22} />}
                                    </ActionIcon>
                                </Tooltip>
                            </div>
                            <Menu.Divider />
                            <Menu.Item
                                icon={<IconLogout size={16} />}
                                onClick={() => {
                                    logout();
                                    onIsNavbarOpenedChange(false);

                                    showNotification({
                                        color: "green",
                                        message: t("signout_success"),
                                    });
                                }}
                            >
                                {t("signout")}
                            </Menu.Item>
                        </Menu.Dropdown>
                    </Menu>
                </Box>
            </Navbar.Section>
        </Navbar>
    );
};
